import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Play,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Boxes,
  Trash2,
  Users,
  FileWarning,
  EyeOff,
  Building2,
  ChefHat,
  Hotel,
  Sparkles,
  ShieldCheck,
  Cpu,
  Lock,
  Cloud,
  ScrollText,
  RefreshCcw,
  Network,
  Star,
  Quote,
  Bell,
  Brain,
  PiggyBank,
  Calculator,
  Compass,
} from "lucide-react";
import {
  MaxWidth,
  Eyebrow,
  SectionTitle,
  SectionLead,
  VerticalCard,
} from "@/components/marketing/Section";

const APP_URL = (process.env.REACT_APP_APP_URL || "").replace(/\/$/, "");

export default function Home() {
  return (
    <>
      <Hero />
      <TrustKPIs />
      <ProfitImpact />
      <Differentiator />
      <Verticals />
      <GuestPayments />
      <AIAssistant />
      <Testimonials />
      <TrustBadges />
      <FinalCTA />
    </>
  );
}

/* ═════════════════════ HERO ═════════════════════ */
function Hero() {
  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden text-white -mt-[68px]"
      style={{
        background:
          "radial-gradient(at 0% 0%, rgba(13,148,136,0.18) 0px, transparent 55%), radial-gradient(at 100% 100%, rgba(13,148,136,0.10) 0px, transparent 50%), linear-gradient(140deg, #0B1B2C 0%, #102A43 55%, #0F172A 100%)",
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <MaxWidth className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 pt-[132px] pb-20 sm:pt-[148px] sm:pb-28">
        <div className="lg:col-span-7 max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[11px] uppercase tracking-widest text-[#86E5DC]">
            <Sparkles className="w-3 h-3" />
            Hospitality SaaS Suite — Made in Italy
          </div>
          <h1
            data-testid="home-hero-headline"
            className="font-heading text-5xl sm:text-6xl lg:text-[68px] font-black leading-[1.02] tracking-tight mt-6"
          >
            Controlla i costi.
            <br />
            <span className="text-[#5EEAD4]">Massimizza il profitto.</span>
          </h1>
          <p
            data-testid="home-hero-sub"
            className="mt-6 text-[17px] sm:text-lg text-white/75 leading-relaxed max-w-lg"
          >
            La piattaforma SaaS per <strong className="text-white">ristoranti e hotel</strong> che
            trasforma sprechi, dati confusi e caos operativo in{" "}
            <strong className="text-white">margini più sani</strong> e decisioni intelligenti.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={`${APP_URL}/pricing`}
              data-testid="home-cta-primary"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg bg-[#0D9488] hover:bg-[#0F766E] text-white font-semibold shadow-[0_10px_30px_-10px_rgba(13,148,136,0.6)] transition-colors"
            >
              Inizia prova gratuita
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/demo"
              data-testid="home-cta-secondary"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg border border-white/20 text-white hover:bg-white/8 font-semibold transition-colors"
            >
              <Play className="w-4 h-4" />
              Guarda la demo
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-6 text-[13px] text-white/55">
            {[
              "14 giorni gratis",
              "Nessuna carta",
              "Setup rapido",
              "Cancella in 1 click",
            ].map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0D9488]" />
                {b}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <HeroDashboard />
        </div>
      </MaxWidth>
    </section>
  );
}

/** Operational dashboard mock — credible hospitality KPIs (food cost, RevPAR, occupancy, alerts). */
function HeroDashboard() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-[#0D9488]/15 blur-3xl rounded-full" />
      <div className="relative rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-md shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)] p-4">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 mb-3">
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <div className="ml-auto text-[10px] uppercase tracking-widest text-white/40">
            costcontrolx.app/dashboard
          </div>
        </div>

        {/* 3 main KPIs */}
        <div className="grid grid-cols-3 gap-2 mb-2.5">
          <KpiTile label="Food cost" value="27,8%" trend="−1,4 pt" color="up" />
          <KpiTile label="Margine mese" value="€ 41,2k" trend="+8,6%" color="up" />
          <KpiTile label="Sprechi" value="€ 612" trend="−22%" color="up" />
        </div>

        {/* Hotel row */}
        <div className="grid grid-cols-3 gap-2 mb-2.5">
          <KpiTile label="Occupazione" value="84%" trend="+3 pt" color="up" small />
          <KpiTile label="RevPAR" value="€ 142" trend="+€11" color="up" small />
          <KpiTile label="ADR" value="€ 168" trend="+4,1%" color="up" small />
        </div>

        {/* Chart bars (12 months margin) */}
        <div className="rounded-lg bg-white/[0.05] border border-white/10 p-3 mb-2.5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/55">
              Margine operativo · 12 mesi
            </div>
            <div className="text-[10px] text-[#5EEAD4] font-semibold">
              +18% YoY
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-20">
            {[42, 48, 38, 55, 60, 52, 65, 72, 68, 74, 80, 88].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm transition-all"
                style={{
                  height: `${h}%`,
                  background:
                    i >= 9
                      ? "linear-gradient(180deg, #5EEAD4 0%, #0D9488 100%)"
                      : "rgba(255,255,255,0.13)",
                }}
              />
            ))}
          </div>
        </div>

        {/* Operative alerts list */}
        <div className="rounded-lg bg-white/[0.04] border border-white/10 divide-y divide-white/8">
          {[
            { l: "Tavolo 7", v: "Pronto · attesa 12'", c: "#FBBF24", icon: Bell },
            { l: "Baccalà · sotto scorta", v: "0,8 kg", c: "#F87171", icon: AlertTriangle },
            { l: "Camera 204", v: "Check-in 16:00", c: "#5EEAD4", icon: Hotel },
            { l: "Ordine Riso · Galli", v: "Consegna domani", c: "#5EEAD4", icon: Boxes },
            { l: "Costo bistecca aumentato", v: "+3,4% · rivedi prezzo", c: "#FBBF24", icon: TrendingUp },
          ].map((r, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-3 py-2 text-[11px]"
            >
              <span className="inline-flex items-center gap-2 text-white/80">
                <r.icon
                  className="w-3 h-3 flex-shrink-0"
                  style={{ color: r.c }}
                />
                {r.l}
              </span>
              <span
                className="font-semibold tabular-nums text-right"
                style={{ color: r.c }}
              >
                {r.v}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, trend, color = "up", small = false }) {
  const trendColor = color === "up" ? "#5EEAD4" : "#F87171";
  return (
    <div className="rounded-lg bg-white/[0.05] border border-white/10 p-3">
      <div className="text-[9.5px] uppercase tracking-widest text-white/45">
        {label}
      </div>
      <div
        className={`font-heading ${
          small ? "text-base" : "text-lg"
        } font-bold mt-0.5 text-white tabular-nums`}
      >
        {value}
      </div>
      <div
        className="text-[10px] font-semibold mt-0.5 tabular-nums"
        style={{ color: trendColor }}
      >
        {trend}
      </div>
    </div>
  );
}

/* ═════════════════════ TRUST KPIs ═════════════════════ */
function TrustKPIs() {
  return (
    <section
      data-testid="home-trust-kpis"
      className="relative border-y border-[#E2E8F0] bg-white"
    >
      <MaxWidth className="py-12 sm:py-14">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#64748B] font-semibold">
            Scelto da chi lavora davvero nell'hospitality
          </p>
        </div>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6">
          {[
            { v: "+18%", l: "marginalità media", c: "#0D9488" },
            { v: "−22%", l: "sprechi cucina", c: "#0D9488" },
            { v: "−11h", l: "ore operative / settimana", c: "#0D9488" },
            { v: "+9%", l: "ricavi medi per coperto", c: "#0D9488" },
          ].map((k) => (
            <div key={k.l} className="text-center">
              <div
                className="font-heading text-4xl sm:text-5xl font-black tabular-nums tracking-tight"
                style={{ color: k.c }}
              >
                {k.v}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[#64748B] mt-1.5">
                {k.l}
              </div>
            </div>
          ))}
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ PROFIT IMPACT (problema + impatto + soluzione) ═════════════════════ */
function ProfitImpact() {
  return (
    <section data-testid="home-profit-impact" className="py-20 sm:py-28 bg-[#F8FAFC]">
      <MaxWidth>
        <div className="max-w-3xl">
          <Eyebrow>Profit leakage</Eyebrow>
          <SectionTitle className="mt-3">
            Quanto stai perdendo ogni mese{" "}
            <span className="text-[#0D9488]">senza saperlo?</span>
          </SectionTitle>
          <SectionLead>
            La maggior parte dei locali e degli hotel perde tra il 4% e il 9% del
            fatturato in sprechi invisibili. CostControlX te li mostra — e ti aiuta a
            chiuderli, uno per uno.
          </SectionLead>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {PROFIT_LEAKAGE.map((p) => (
            <ImpactCard key={p.id} {...p} />
          ))}
        </div>
      </MaxWidth>
    </section>
  );
}

function ImpactCard({ icon: Icon, problem, impact, solution }) {
  return (
    <div className="group bg-white rounded-2xl border border-[#E2E8F0] p-6 transition-all hover:shadow-[0_18px_36px_-15px_rgba(15,23,42,0.15)] hover:-translate-y-0.5 hover:border-[#0D9488]/40 flex flex-col">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#B91C1C] inline-flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#B91C1C] font-bold">
          Problema
        </div>
      </div>
      <h3 className="font-heading text-base font-bold text-[#0F172A] tracking-tight mt-3 leading-tight">
        {problem}
      </h3>
      <div className="mt-4 pt-3 border-t border-dashed border-[#E2E8F0]">
        <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-semibold">
          Impatto economico
        </div>
        <div className="font-heading text-2xl font-black text-[#B91C1C] mt-0.5 tabular-nums">
          {impact}
        </div>
      </div>
      <div className="mt-4 flex-1 flex">
        <div className="bg-[#E0F2F1] border border-[#0D9488]/20 rounded-lg p-3 w-full">
          <div className="text-[10px] uppercase tracking-widest text-[#0F766E] font-bold">
            Con CostControlX
          </div>
          <p className="text-[13px] text-[#0F172A] mt-1 leading-snug">
            {solution}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════ DIFFERENTIATOR ═════════════════════ */
function Differentiator() {
  return (
    <section data-testid="home-differentiator" className="py-20 sm:py-28">
      <MaxWidth className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Eyebrow>Il differenziatore</Eyebrow>
          <SectionTitle className="mt-3">
            Il primo gestionale che parte dal{" "}
            <span className="text-[#0D9488]">margine.</span>
          </SectionTitle>
          <SectionLead>
            Non è un POS che fa un po' di analytics. Non è un PMS che fa un po' di
            fatturazione. CostControlX è una <strong className="text-[#0F172A]">piattaforma decisionale
            hospitality</strong> costruita intorno a una sola domanda:{" "}
            <em className="text-[#0F766E] not-italic font-semibold">"quanto guadagno davvero?"</em>
          </SectionLead>

          <ul className="mt-7 space-y-3">
            {[
              { icon: Calculator, t: "Margine per piatto", body: "Ricetta × prezzo fornitore × resa = costo reale, sempre aggiornato." },
              { icon: Hotel, t: "Margine per camera", body: "Costo housekeeping + breakfast + extra vs ricavo netto della prenotazione." },
              { icon: PiggyBank, t: "Profitto per servizio", body: "Pranzo, cena, brunch, eventi: vedi quale servizio ti rende davvero." },
              { icon: Compass, t: "Analytics unificata", body: "Una sola dashboard. Una sola verità. Niente più riconciliazioni Excel." },
            ].map((it) => (
              <li key={it.t} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#E0F2F1] text-[#0F766E] inline-flex items-center justify-center flex-shrink-0">
                  <it.icon className="w-4 h-4" strokeWidth={2.4} />
                </span>
                <div>
                  <div className="font-semibold text-[#0F172A]">{it.t}</div>
                  <p className="text-sm text-[#475569] mt-0.5 leading-relaxed">
                    {it.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#102A43] hover:bg-[#0B1B2C] text-white font-semibold transition-colors"
            >
              Esplora tutte le funzionalità
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Side mock — margine breakdown */}
        <div className="lg:col-span-6">
          <MarginBreakdown />
        </div>
      </MaxWidth>
    </section>
  );
}

function MarginBreakdown() {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E8F0] shadow-[0_25px_50px_-25px_rgba(15,23,42,0.15)] p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-semibold">
            Marginalità piatti · oggi
          </div>
          <div className="font-heading text-lg font-bold text-[#0F172A] mt-0.5">
            Top 5 per profitto netto
          </div>
        </div>
        <div className="px-2.5 py-1 rounded-md bg-[#E0F2F1] text-[#0F766E] text-[10px] font-bold uppercase tracking-widest">
          oggi
        </div>
      </div>
      <div className="space-y-3.5">
        {[
          { name: "Tagliata di manzo 250g", price: "€ 26,00", cost: "€ 8,40", margin: 67, color: "#0D9488" },
          { name: "Risotto allo zafferano", price: "€ 18,00", cost: "€ 4,10", margin: 77, color: "#0D9488" },
          { name: "Spaghetti alle vongole", price: "€ 16,00", cost: "€ 6,90", margin: 56, color: "#0F766E" },
          { name: "Pizza Margherita", price: "€ 9,00", cost: "€ 2,30", margin: 74, color: "#0D9488" },
          { name: "Tartare di tonno", price: "€ 22,00", cost: "€ 11,80", margin: 46, color: "#FBBF24" },
        ].map((d, i) => (
          <div key={i}>
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-[#0F172A] font-medium truncate pr-2">
                {d.name}
              </span>
              <span className="text-[#64748B] tabular-nums whitespace-nowrap">
                {d.price} <span className="text-[#94A3B8]">- {d.cost}</span>
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${d.margin}%`,
                    backgroundColor: d.color,
                  }}
                />
              </div>
              <span
                className="text-[11px] font-bold tabular-nums w-9 text-right"
                style={{ color: d.color }}
              >
                {d.margin}%
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-[#E2E8F0] flex items-baseline justify-between">
        <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">
          Margine medio
        </div>
        <div className="font-heading text-xl font-black text-[#0F766E] tabular-nums">
          64%
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════ VERTICALS ═════════════════════ */
function Verticals() {
  return (
    <section
      data-testid="home-verticals"
      className="py-20 sm:py-28 bg-gradient-to-b from-[#F8FAFC] to-white"
    >
      <MaxWidth>
        <div className="max-w-3xl">
          <Eyebrow>Soluzioni dedicate</Eyebrow>
          <SectionTitle className="mt-3">
            Costruito per il tuo settore. Non un generico gestionale.
          </SectionTitle>
          <SectionLead>
            Quattro suite specializzate sullo stesso motore SaaS, con pricing
            chiaro e nessun setup tecnico.
          </SectionLead>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {VERTICALS.map((v) => (
            <VerticalCard key={v.title} {...v} />
          ))}
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ GUEST PAYMENTS / CARTA A GARANZIA ═════════════════════ */
function GuestPayments() {
  return (
    <section
      data-testid="home-guest-payments"
      className="py-20 sm:py-28"
      style={{
        background:
          "linear-gradient(180deg, #F7F7F5 0%, #FFFFFF 100%)",
      }}
    >
      <MaxWidth>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Copy column */}
          <div className="lg:col-span-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#C9A15D]/40 bg-[#C9A15D]/10 text-[11px] uppercase tracking-widest text-[#8A6B2A] font-semibold"
              data-testid="home-guest-payments-badge"
            >
              <ShieldCheck className="w-3 h-3" />
              Foundation pronta · Provider-per-tenant
            </div>
            <SectionTitle className="mt-4">
              Carta a garanzia per prenotazioni,
              <br />
              <span className="text-[#0D9488]">eventi e no-show.</span>
            </SectionTitle>
            <p className="mt-5 text-[15px] sm:text-base text-[#475569] leading-relaxed max-w-xl">
              CostControlX permette alle strutture di gestire una carta a garanzia
              per prenotazioni tavolo, eventi, gruppi o soggiorni. La carta viene
              raccolta tramite il provider di pagamento collegato dal tenant, come
              Stripe Connect. <strong className="text-[#0F172A]">CostControlX non
              salva PAN, CVV o dati sensibili della carta.</strong>
            </p>

            <ul className="mt-6 space-y-3 text-[14.5px] text-[#334155]" data-testid="home-guest-payments-points">
              {[
                "Ogni struttura collega il proprio account Stripe Connect.",
                "La carta può essere usata come garanzia o per una preautorizzazione, dove configurata.",
                "Eventuali no-show fee o addebiti vengono eseguiti solo manualmente da un operatore autorizzato.",
                "La struttura deve usare una policy di cancellazione/no-show chiara e approvata.",
                "CostControlX registra token, stato garanzia e audit operativo, non dati carta completi.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#0D9488] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-white px-4 py-3 text-[12.5px] text-[#475569] leading-relaxed">
              <strong className="text-[#102A43]">Nota:</strong> le funzioni di
              pagamento ospiti richiedono configurazione provider, policy approvata
              e abilitazione tenant. Nessun addebito è automatico.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`${APP_URL}/pricing`}
                data-testid="home-guest-payments-cta-primary"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#102A43] hover:bg-[#0B1B2C] text-white font-semibold transition-colors"
              >
                Inizia prova gratuita
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/features"
                data-testid="home-guest-payments-cta-secondary"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#C9A15D] text-[#8A6B2A] hover:bg-[#C9A15D]/10 font-semibold transition-colors"
              >
                Scopri come funziona
              </Link>
            </div>
          </div>

          {/* Visual: provider-per-tenant diagram */}
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)]">
              <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">
                Provider per tenant
              </div>
              <h3 className="mt-2 text-xl font-heading font-bold text-[#0F172A]">
                Ogni struttura mantiene il proprio provider.
              </h3>
              <p className="mt-3 text-[13.5px] text-[#475569] leading-relaxed">
                CostControlX si collega al provider della struttura e mostra
                stato, onboarding e disponibilità operativa. Questo mantiene
                separati i flussi dei singoli tenant e riduce rischi operativi.
              </p>

              {/* Diagram */}
              <div className="mt-5 space-y-3">
                {[
                  { name: "Ristorante Bella Napoli", status: "Connesso · Sandbox", tone: "connected" },
                  { name: "Hotel Aurora", status: "Connesso · Sandbox", tone: "connected" },
                  { name: "Trattoria da Marco", status: "Non collegato", tone: "off" },
                ].map((t) => (
                  <div
                    key={t.name}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className={
                          "w-8 h-8 rounded-md flex-shrink-0 inline-flex items-center justify-center " +
                          (t.tone === "connected"
                            ? "bg-[#0D9488]/10 text-[#0D9488]"
                            : "bg-[#F1F5F9] text-[#94A3B8]")
                        }
                      >
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-[#0F172A] truncate">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#64748B]">
                          Stripe Connect · sandbox
                        </div>
                      </div>
                    </div>
                    <span
                      className={
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 " +
                        (t.tone === "connected"
                          ? "bg-[#0D9488]/10 text-[#0F766E] border border-[#0D9488]/30"
                          : "bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]")
                      }
                    >
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-start gap-2 rounded-md bg-[#F7F7F5] border border-[#C9A15D]/50 px-3 py-2 text-[11.5px] text-[#102A43]">
                <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#0D9488]" />
                <span>
                  Zero dati carta in CostControlX. Le carte sono raccolte e
                  custodite dal provider Stripe. Solo token e stato safe vengono
                  salvati per l&apos;audit operativo.
                </span>
              </div>
            </div>
          </div>
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ AI ═════════════════════ */
function AIAssistant() {
  return (
    <section
      data-testid="home-ai"
      className="py-20 sm:py-28 text-white"
      style={{
        background:
          "radial-gradient(at 80% 30%, rgba(13,148,136,0.20) 0px, transparent 50%), linear-gradient(180deg, #0F172A 0%, #102A43 100%)",
      }}
    >
      <MaxWidth className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Eyebrow color="teal">Intelligence — foundation & roadmap</Eyebrow>
          <SectionTitle color="white" className="mt-3">
            Analytics oggi.{" "}
            <span className="text-[#5EEAD4]">AI in roadmap.</span>
          </SectionTitle>
          <SectionLead color="white">
            La foundation analitica è già operativa: margini reali per piatto e
            per camera, anomalie listini fornitori, KPI hotel live. Le funzioni
            di forecast predittivo e controller AI con alert proattivi sono in
            roadmap — qui sotto un'anteprima di cosa arriverà.
          </SectionLead>
          <ul className="mt-7 space-y-3 text-[15px]">
            {[
              { text: "Analytics marginalità live per servizio", live: true },
              { text: "Anomalie listini fornitori vs storico", live: true },
              { text: "KPI hotel (Occupancy / ADR / RevPAR) live", live: true },
              { text: "Forecast predittivo occupazione e coperti", live: false },
              { text: "Suggerimenti AI piatti / pricing / fornitori", live: false },
              { text: "Controller AI con alert proattivi", live: false },
            ].map((b) => (
              <li key={b.text} className="flex items-start gap-3 text-white/85">
                <span
                  className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    b.live ? "bg-[#5EEAD4]" : "bg-[#FBBF24]"
                  }`}
                />
                <span className="flex-1">
                  {b.text}
                  {!b.live && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold">
                      Roadmap
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Preview card — clearly tagged as roadmap so visitors do not
            mistake it for a live screenshot. */}
        <div className="lg:col-span-7">
          <div className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-md p-5 sm:p-6 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
              <div className="inline-flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#5EEAD4] to-[#0D9488] inline-flex items-center justify-center">
                  <Brain className="w-4.5 h-4.5 text-[#0F172A]" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="font-semibold text-white text-sm">
                    CostControlX AI — anteprima
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#5EEAD4]">
                    Preview · funzionalità in roadmap
                  </div>
                </div>
              </div>
              <div className="px-2 py-0.5 rounded-full bg-[#FBBF24]/20 border border-[#FBBF24]/40 text-[10px] font-bold text-[#FBBF24] uppercase tracking-widest">
                Roadmap
              </div>
            </div>
            <div className="space-y-3 text-[14px] text-white/85 leading-relaxed">
              {AI_BRIEFINGS.map((msg, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-white/[0.04] border border-white/10 p-3.5"
                >
                  <div className="flex items-start gap-2 mb-1.5">
                    <msg.icon
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: msg.color }}
                    />
                    <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: msg.color }}>
                      {msg.tag}
                    </div>
                  </div>
                  <p>{msg.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-white/55 leading-relaxed">
              Esempio illustrativo. Le funzioni di suggerimento automatico
              mostrate qui sopra sono in roadmap; la foundation analitica
              sottostante (margini, costi, KPI) è già operativa nei piani
              Professional+.
            </div>
          </div>
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ TESTIMONIALS ═════════════════════ */
function Testimonials() {
  return (
    <section data-testid="home-testimonials" className="py-20 sm:py-28">
      <MaxWidth>
        <div className="max-w-3xl">
          <Eyebrow>Storie reali di operatori reali</Eyebrow>
          <SectionTitle className="mt-3">
            Quello che cambia quando vedi davvero il margine.
          </SectionTitle>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              className="bg-white rounded-2xl border border-[#E2E8F0] p-7 flex flex-col hover:shadow-[0_18px_36px_-15px_rgba(15,23,42,0.15)] transition-all"
            >
              <Quote className="w-6 h-6 text-[#0D9488] opacity-50" />
              <blockquote className="font-heading text-[18px] sm:text-[19px] font-semibold leading-snug text-[#0F172A] mt-3 flex-1">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-5 pt-5 border-t border-[#E2E8F0] flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full inline-flex items-center justify-center text-white font-bold text-sm"
                  style={{ background: t.color }}
                >
                  {t.initials}
                </div>
                <div>
                  <div className="font-semibold text-[#0F172A] text-sm">
                    {t.author}
                  </div>
                  <div className="text-[12px] text-[#64748B]">{t.role}</div>
                </div>
                <div className="ml-auto inline-flex">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className="w-3.5 h-3.5 text-[#FBBF24] fill-[#FBBF24]"
                    />
                  ))}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ TRUST BADGES ═════════════════════ */
function TrustBadges() {
  return (
    <section
      data-testid="home-trust"
      className="py-20 sm:py-24 bg-[#0F172A] text-white"
    >
      <MaxWidth>
        <div className="max-w-2xl">
          <Eyebrow color="white">Sicurezza & compliance</Eyebrow>
          <SectionTitle color="white" className="mt-3">
            Standard enterprise. Anche se hai{" "}
            <span className="text-[#5EEAD4]">una sola sede.</span>
          </SectionTitle>
          <SectionLead color="white">
            Infrastrutture cloud certificate, pagamenti Stripe PCI-DSS,
            isolamento multi-tenant e audit log append-only e tracciabilità operativa per ogni azione.
          </SectionLead>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-12">
          {[
            { icon: ShieldCheck, label: "Privacy & GDPR-ready" },
            { icon: Lock, label: "Stripe PCI-DSS" },
            { icon: Cloud, label: "Cloud secure" },
            { icon: Network, label: "Multi-tenant isolato" },
            { icon: ScrollText, label: "Audit logs" },
            { icon: RefreshCcw, label: "Backup automatici" },
          ].map((b) => (
            <div
              key={b.label}
              className="rounded-xl bg-white/[0.04] border border-white/10 px-4 py-5 text-center hover:bg-white/[0.07] transition-colors"
            >
              <b.icon className="w-5 h-5 text-[#0D9488] mx-auto" strokeWidth={2.2} />
              <div className="mt-3 text-[11px] uppercase tracking-widest text-white/75 font-semibold">
                {b.label}
              </div>
            </div>
          ))}
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ FINAL CTA ═════════════════════ */
function FinalCTA() {
  return (
    <section data-testid="home-final-cta" className="py-20 sm:py-28">
      <MaxWidth>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#102A43] via-[#0F172A] to-[#0F766E] p-10 sm:p-16 text-center text-white">
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle, white 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
          <div className="relative max-w-3xl mx-auto">
            <Eyebrow color="white">Pronto a iniziare</Eyebrow>
            <SectionTitle color="white" className="mt-4">
              14 giorni di prova gratuita.
              <br />
              <span className="text-[#5EEAD4]">Zero carta. Zero rischio.</span>
            </SectionTitle>
            <p className="mt-5 text-white/80 text-lg">
              Inizia in 2 minuti. Importa il tuo menu o le tue camere e vedi
              subito dove il margine si nasconde.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`${APP_URL}/pricing`}
                data-testid="home-final-cta-signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-lg bg-[#0D9488] hover:bg-[#0F766E] active:bg-[#0B5F56] text-white font-semibold shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#102A43] focus-visible:ring-[#5EEAD4] transition-colors"
              >
                Inizia prova gratuita
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-lg border border-white/25 text-white hover:bg-white/10 font-semibold transition-colors"
              >
                Vedi i piani
              </Link>
            </div>
          </div>
        </div>
      </MaxWidth>
    </section>
  );
}

/* ═════════════════════ STATIC CONTENT ═════════════════════ */

const PROFIT_LEAKAGE = [
  {
    id: "food-cost",
    icon: AlertTriangle,
    problem: "Food cost fuori controllo",
    impact: "−€ 1.200 / mese",
    solution: "Ricetta × prezzo fornitore × resa: vedi subito quali piatti perdono margine ad ogni servizio.",
  },
  {
    id: "stock",
    icon: Boxes,
    problem: "Magazzino fermo e prodotti scaduti",
    impact: "−€ 780 / mese",
    solution: "Scarico automatico da comande + alert lotti in scadenza. Ordini, comande e scarti aggiornano le quantità operative: inventario più veloce e giacenze sempre leggibili.",
  },
  {
    id: "waste",
    icon: Trash2,
    problem: "Sprechi invisibili in cucina",
    impact: "−€ 410 / mese",
    solution: "Tracciamento scarti per turno e per chef. Vedi dove il margine evapora — e perché.",
  },
  {
    id: "suppliers",
    icon: FileWarning,
    problem: "Fornitori disallineati",
    impact: "+3-7% sui prezzi",
    solution: "Listini archiviati, alert su variazioni anomale, suggerimenti di rinegoziazione.",
  },
  {
    id: "kpi",
    icon: EyeOff,
    problem: "KPI sparsi tra Excel e WhatsApp",
    impact: "5+ ore / settimana",
    solution: "Un'unica dashboard. Una sola verità. Niente più riconciliazioni manuali.",
  },
  {
    id: "booking",
    icon: TrendingDown,
    problem: "Prenotazioni scollegate dal POS",
    impact: "−2-4 coperti / sera",
    solution: "Reservation + sala + comande integrate. Sai sempre quanti coperti hai e a che orario.",
  },
  {
    id: "fb-rooms",
    icon: Building2,
    problem: "Camere e ristorante separati",
    impact: "−€ 6 per ospite",
    solution: "Conto camera + F&B unico. Addebiti automatici. Margine per ospite calcolato realmente.",
  },
  {
    id: "margin",
    icon: TrendingUp,
    problem: "Nessuna visione dei margini reali",
    impact: "Decisioni al buio",
    solution: "P&L operativo live. Sai cosa hai guadagnato — non solo cosa hai incassato.",
  },
];

const VERTICALS = [
  {
    tag: "Restaurant",
    title: "Per ristoranti e pizzerie",
    subtitle: "Food cost, menu engineering, KPI sala, magazzino, ordini.",
    points: ["Ricette e food cost su ricetta reale", "POS + stampa comande cucina/bar", "Analytics marginalità per piatto"],
    ctaTo: "/restaurant",
    ctaLabel: "Scopri Restaurant",
    accent: true,
    testId: "vertical-restaurant",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=900&q=70",
  },
  {
    tag: "Hotel",
    title: "Per hotel e resort",
    subtitle: "Camere, occupancy, revenue, OTA, housekeeping.",
    points: ["Gestione camere + housekeeping", "Configurazione canali OTA (Booking, Expedia, Airbnb)", "Revenue dashboard ADR/RevPAR"],
    ctaTo: "/hotel",
    ctaLabel: "Scopri Hotel",
    testId: "vertical-hotel",
    image: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=900&q=70",
  },
  {
    tag: "Combo",
    title: "Hotel con ristorazione",
    subtitle: "Tutto integrato: camere, ristorante, breakfast, room service.",
    points: ["Camere + POS unificati", "Breakfast e room service tracciati", "Margine globale per ospite"],
    ctaTo: "/combo",
    ctaLabel: "Scopri Combo",
    testId: "vertical-combo",
    image: "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=900&q=70",
  },
  {
    tag: "Enterprise",
    title: "Per gruppi e catene",
    subtitle: "Dashboard centralizzata, benchmark, ruoli, audit log.",
    points: ["Multi-location e benchmark", "Ruoli granulari + audit log", "SSO e supporto dedicato"],
    ctaTo: "/enterprise",
    ctaLabel: "Scopri Enterprise",
    testId: "vertical-enterprise",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=70",
  },
];

const AI_BRIEFINGS = [
  {
    icon: TrendingUp,
    color: "#5EEAD4",
    tag: "Margine ↑",
    body: "Il risotto allo zafferano vende il 18% in meno della media. Il margine è 77%. Suggerimento: posizionalo come piatto del giorno mercoledì e venerdì.",
  },
  {
    icon: AlertTriangle,
    color: "#FBBF24",
    tag: "Allerta fornitore",
    body: "Galli ha aumentato il prezzo della tartare di tonno del +14% rispetto al listino di marzo. Conviene richiedere un preventivo a Pesca Adriatica entro fine settimana.",
  },
  {
    icon: Boxes,
    color: "#5EEAD4",
    tag: "Riordino",
    body: "Sulla base del venduto degli ultimi 14 giorni: ordinare 12kg di farina tipo 00, 8kg di pomodoro pelato e 4 cassette di basilico. Costo previsto: € 184,20.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Abbiamo ridotto gli sprechi del 21% nei primi 60 giorni. Il vero valore? Sapere finalmente quanto ci costa ogni piatto.",
    author: "Bella Napoli Group",
    role: "Pizzeria · Napoli",
    initials: "BN",
    color: "#0F766E",
  },
  {
    quote:
      "Finalmente food cost e camere nella stessa dashboard. Prima usavamo tre software diversi e nessuno parlava con gli altri.",
    author: "Resort Demo Collection",
    role: "Resort 4★ · Costa Smeralda",
    initials: "RM",
    color: "#102A43",
  },
  {
    quote:
      "Gli analytics di marginalità mi hanno fatto rinegoziare due fornitori. Solo questo si è ripagato il canone annuale tre volte.",
    author: "Osteria Mediterraneo",
    role: "Ristorante · Milano",
    initials: "OM",
    color: "#0D9488",
  },
];
